.screen {
	min-height: 100%;
	padding: var(--space-5) var(--space-5) var(--space-7);
	max-width: var(--screen-max);
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	gap: var(--space-5);
}

/* Header back button — square icon control sized for the header bar. */
.back {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border: none;
	background: none;
	border-radius: var(--radius-sm);
	color: var(--ink-muted);
	cursor: pointer;
}
.back:hover {
	color: var(--ink);
	background: var(--surface-input);
}

.hero {
	position: relative;
	width: calc(100% + 2 * var(--space-5));
	margin: 0 calc(-1 * var(--space-5));
	height: 240px;
	overflow: hidden;
	background: var(--surface-input);
}
.heroImg {
	width: 100%;
	height: 100%;
	object-fit: cover;
	display: block;
}

/* Above the screen max-width the detail card insets from the viewport, so the
   hero is no longer edge-to-edge — round its corners to match the cards. */
@media (min-width: 640px) {
	.hero {
		border-radius: var(--radius-lg);
	}
}

.body {
	display: flex;
	flex-direction: column;
	gap: var(--space-5);
}

/* Both states carry the same padding + 1px border so the hover box and the
   edit box are the same size. Pulling that offset back off the left edge
   lines the title up with the content below it, in either state — the box
   bleeds into the page gutter rather than indenting the text. */
.title,
.titleInput {
	font-size: 1.5rem;
	font-weight: 700;
	margin: 0 0 0 calc(-1 * (var(--space-2) + 1px));
	padding: var(--space-2);
	border-radius: var(--radius-sm);
	color: var(--ink);
	font-family: inherit;
}
.title {
	background: none;
	border: 1px solid transparent;
}
/* Not while offline, when the body fieldset is disabled. */
.body:not(:disabled) .title:hover {
	border-color: color-mix(in srgb, var(--ink) 12%, transparent);
	cursor: text;
}
.titleInput {
	border: 1px solid var(--accent-sage);
	background: var(--surface-input);
}

.meta {
	display: flex;
	gap: var(--space-4);
	flex-wrap: wrap;
	align-items: center;
	font-size: 0.85rem;
	color: var(--ink-muted);
}
.meta a {
	color: var(--accent-sage);
}
/* Push the times edit toggle to the right of whatever else the row holds,
   matching the section heads below. */
.metaEdit {
	margin-left: auto;
}

.tags {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
	gap: var(--space-3);
	padding: var(--space-4);
	background: var(--surface-card);
	border-radius: var(--radius-md);
}
.tagField {
	display: flex;
	flex-direction: column;
	gap: 4px;
	font-size: 0.85rem;
}
.tagLabel {
	color: var(--ink-muted);
	text-transform: uppercase;
	letter-spacing: 0.05em;
	font-size: 0.7rem;
}
.tagSelect {
	padding: var(--space-2);
	border: 1px solid color-mix(in srgb, var(--ink) 12%, transparent);
	border-radius: var(--radius-sm);
	background: var(--surface-input);
	color: var(--ink);
	font: inherit;
}

.tagsEmpty {
	padding: var(--space-4);
	background: var(--surface-card);
	border-radius: var(--radius-md);
	color: var(--ink-muted);
	font-size: 0.9rem;
}

/* Tag section header: title on the left, edit toggle (+ transient "Saved")
   on the right. */
.tagsHead {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--space-3);
	margin-bottom: var(--space-3);
}
.tagsHead h2 {
	margin: 0;
}
.tagsActions {
	display: flex;
	align-items: center;
	gap: var(--space-3);
}
.editTags {
	padding: 0;
	background: none;
	border: none;
	color: var(--accent-sage);
	font: inherit;
	font-size: 0.85rem;
	cursor: pointer;
}
.saved {
	font-size: 0.8rem;
	color: var(--ink-muted);
}

.pillRow {
	display: flex;
	flex-wrap: wrap;
	gap: var(--space-2);
}
.pill {
	font-size: 0.8rem;
	font-weight: 500;
	padding: 3px 10px;
	border-radius: 999px;
	color: white;
}

.section h2 {
	font-size: 1rem;
	font-weight: 600;
	margin: 0 0 var(--space-3);
	color: var(--ink);
}
.list {
	margin: 0;
	padding-left: var(--space-5);
	display: flex;
	flex-direction: column;
	gap: var(--space-2);
	color: var(--ink);
	line-height: 1.5;
}
.subsection + .subsection {
	margin-top: var(--space-4);
}
.subheading {
	font-size: 0.85rem;
	font-weight: 600;
	margin: 0 0 var(--space-2);
	color: var(--ink-muted);
}
.sectionHead {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--space-3);
	margin-bottom: var(--space-3);
}
.sectionHead h2 {
	margin: 0;
}
.editBtn {
	padding: 0;
	background: none;
	border: none;
	color: var(--accent-sage);
	font: inherit;
	font-size: 0.85rem;
	cursor: pointer;
}

/* The two whole-recipe actions, closing the page. Delete is pushed to the far
   end so the destructive control isn't a neighbouring tap target. */
.footActions {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: var(--space-3);
	margin-top: var(--space-4);
}

/* Matched outline buttons — same box, accent apart. */
.footAction,
.delete {
	display: inline-flex;
	align-items: center;
	gap: var(--space-2);
	padding: var(--space-2) var(--space-4);
	background: transparent;
	border: 1px solid;
	border-radius: var(--radius-md);
	font: inherit;
	font-size: 0.9rem;
	font-weight: 600;
	cursor: pointer;
}

.footAction {
	border-color: color-mix(in srgb, var(--accent-sage) 40%, transparent);
	color: var(--accent-sage);
}
.footAction:hover {
	background: color-mix(in srgb, var(--accent-sage) 8%, transparent);
}

.delete {
	margin-left: auto;
	border-color: color-mix(in srgb, var(--accent-rose) 40%, transparent);
	color: var(--accent-rose);
}
.delete:hover {
	background: color-mix(in srgb, var(--accent-rose) 8%, transparent);
}

/* Offline: the body fieldset disables its controls, and the actions fade. */
.editBtn:disabled,
.editTags:disabled,
.footAction:disabled,
.delete:disabled {
	opacity: 0.5;
}
